import type { Appointment, DayFrame } from "@dentacore/shared-types";

/**
 * Where a minute sits on the day grid.
 *
 * **Separated from the component because this is the part that can be wrong.**
 * A block at the wrong pixel is STORY-032's only real failure mode, and it is
 * the one thing a screenshot cannot tell you about — so the arithmetic lives
 * here, in functions a test can hold against a stated expectation, rather than
 * inline in JSX where it can only be checked by looking.
 */

/**
 * Pixels per minute — the mockup's `PXMIN`, and the number the whole grid is
 * built on. At 1.5, an hour is 90px and the shortest bookable slot is legible.
 *
 * **This is a rendering constant, not clinic data.** It describes the drawing,
 * not the practice: no admin configures it and no clinic disagrees about it.
 */
export const PX_PER_MINUTE = 1.5;

/**
 * The scale the grid actually draws at: the base above times the location's
 * calendar zoom, a percent (STORY-177). 100 is the original 1.5; the default a
 * location starts with is 50.
 */
export function pxPerMinuteFor(zoomPercent: number): number {
  return (PX_PER_MINUTE * zoomPercent) / 100;
}

/** The mockup's time gutter, in px. The axis labels live in it. */
export const GUTTER_PX = 58;

export interface DaySpan {
  /** Minutes since local midnight. Always a whole hour. */
  startsAt: number;
  endsAt: number;
  /** Whole hours from `startsAt` to `endsAt`, for the axis. */
  hours: number[];
  heightPx: number;
  /** The scale this span is drawn at (STORY-177) — what every offset multiplies by. */
  pxPerMinute: number;
}

/**
 * The span the grid draws — **the location's chosen hours, stretched for the
 * day when something falls outside them** (STORY-177, 2026-10-11).
 *
 * T7 made this the whole twenty-four hours, and the grid became far taller than
 * any screen. Anthony: show 08:00–20:00 by default, and let it be set. So the
 * span starts as the location's `calendarOpensAt`–`calendarClosesAt`.
 *
 * **It never hides a booking.** A day with an appointment or a Google block
 * before the start or after the end widens by whole hours, just far enough to
 * hold it — the same promise the old union made, for the same reason: a patient
 * booked at 07:30 whom nobody can see is a patient nobody expects. Other days
 * stay at the chosen hours.
 *
 * What it still does not do: bound what the application *offers* (the server's
 * `offerableBoundsForWeekday`), or say whether a branch is open.
 */
export interface SpanInput {
  opensAt: number;
  closesAt: number;
  /** The calendar zoom, a percent of the original scale. */
  zoom: number;
  /** What the day holds, in minutes since local midnight; may run past 1440. */
  occupied?: readonly { startsAt: number; endsAt: number }[];
}

export function spanFor(input: SpanInput): DaySpan {
  let startsAt = input.opensAt;
  let endsAt = input.closesAt;

  for (const item of input.occupied ?? []) {
    startsAt = Math.min(startsAt, Math.floor(item.startsAt / 60) * 60);
    endsAt = Math.max(endsAt, Math.ceil(item.endsAt / 60) * 60);
  }

  // The axis is one row per hour, and a late booking can end past midnight.
  startsAt = Math.max(0, startsAt);
  endsAt = Math.min(24 * 60, endsAt);

  const hours: number[] = [];
  for (let minute = startsAt; minute <= endsAt; minute += 60) hours.push(minute);

  const pxPerMinute = pxPerMinuteFor(input.zoom);
  return { startsAt, endsAt, hours, heightPx: (endsAt - startsAt) * pxPerMinute, pxPerMinute };
}

export interface Placement {
  /** Minutes since midnight of `date`, in the branch's timezone. */
  startsAt: number;
  endsAt: number;
}

/**
 * An appointment's start and end as minutes on the displayed day, **read on the
 * branch's clock**.
 *
 * The instant is absolute; the grid is a wall in one place. A clinic in Beirut
 * viewed from London draws its 09:00 at 09:00, not at 07:00 — which is why the
 * zone comes from the frame rather than from the reader's browser.
 *
 * Returns null for an appointment that starts on a different local day: the
 * list endpoint windows on UTC, so a branch far enough east or west can be
 * handed a neighbouring day's booking, and drawing it at a nonsense offset is
 * worse than leaving it out.
 *
 * An appointment running past local midnight keeps counting — 00:30 the next
 * morning reads as 1470 — so a late block draws its true length instead of
 * wrapping to the top of the grid.
 */
export function placeOnDay(
  appointment: Appointment,
  timezone: string,
  date: string,
): Placement | null {
  const start = wallClock(appointment.startsAt, timezone);
  if (start.date !== date) return null;

  const end = wallClock(appointment.endsAt, timezone);
  const endsAt = end.date === start.date ? end.minutes : end.minutes + 1440;

  return { startsAt: start.minutes, endsAt };
}

/**
 * An external block's span on the displayed day, **clamped to it**
 * (STORY-049b).
 *
 * ── Why this is not `placeOnDay` ───────────────────────────────────────────
 *
 * `placeOnDay` returns null when a booking starts on another local day, which
 * is right for an appointment: one that starts tomorrow belongs to tomorrow's
 * grid, and drawing it here at a nonsense offset would be worse than leaving it
 * out. An external block is not like that. A week of renovation starts once and
 * closes seven days, and on days two through six it starts *before* the day
 * being drawn and ends *after* it — so the same rule would draw nothing at all
 * on exactly the days the chair is most obviously shut.
 *
 * So this clamps instead of rejecting: the part of the block that falls inside
 * the day, in minutes since local midnight. Null only when it does not touch
 * the day at all, which the widened server window makes possible.
 *
 * **The ends are clamped to 0 and 1440 rather than allowed to run past**, which
 * is the opposite of what an appointment does — a late booking keeps counting
 * to 1470 so it draws its true length. A block has no true length worth
 * preserving here; it is a shape saying *not this time*, and one drawn 300px
 * below the bottom of the grid says nothing at all.
 */
export function placeSpanOnDay(
  span: { startsAt: string; endsAt: string },
  timezone: string,
  date: string,
): Placement | null {
  const dayStart = Date.parse(`${date}T00:00:00.000Z`);
  const start = wallClock(span.startsAt, timezone);
  const end = wallClock(span.endsAt, timezone);

  // Whole days from the displayed date, on the branch's clock. Date arithmetic
  // in UTC on a date-only string is exact — no offset is involved, because both
  // sides have already been resolved to a local calendar date.
  const startDay = Math.round((Date.parse(`${start.date}T00:00:00.000Z`) - dayStart) / 86_400_000);
  const endDay = Math.round((Date.parse(`${end.date}T00:00:00.000Z`) - dayStart) / 86_400_000);

  const startsAt = Math.max(0, startDay * 1440 + start.minutes);
  const endsAt = Math.min(1440, endDay * 1440 + end.minutes);

  if (endsAt <= startsAt) return null;
  return { startsAt, endsAt };
}

/** `10:00` → 600. */
export function minutesOfClock(clock: string): number {
  const [hours, minutes] = clock.split(":");
  return Number(hours) * 60 + Number(minutes);
}

/** 600 → `10:00`. Tabular on the axis, so the labels line up. */
export function clockOfMinutes(minutes: number): string {
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  const hours = String(Math.floor(wrapped / 60)).padStart(2, "0");
  return `${hours}:${String(wrapped % 60).padStart(2, "0")}`;
}

/**
 * An ISO instant as a branch's own wall clock — `2026-08-25T06:15:00Z` → `09:15`.
 *
 * **The branch's zone, not the reader's.** A manager looking at a branch from
 * another country has to see the time the patient will actually walk in, which
 * is the same rule the now-line and the hour gutter already follow. Here rather
 * than in a component because two of them now need it (the reschedule picker
 * and the selection bar), and a second copy is how two renderings of one time
 * begin to disagree.
 */
export function clockAt(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(instant));
}

/**
 * An ISO instant read on a named zone: which local day it lands on, and how
 * many minutes into it.
 *
 * `Intl` rather than arithmetic on the offset, because the offset is not a
 * constant — a clinic on daylight saving moves twice a year, and a grid that
 * hardcoded the offset would be an hour wrong for half of it.
 */
export function wallClock(iso: string, timezone: string): { date: string; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));

  const find = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "00";

  return {
    date: `${find("year")}-${find("month")}-${find("day")}`,
    minutes: Number(find("hour")) * 60 + Number(find("minute")),
  };
}

/**
 * Now, on the branch's clock, if now is on the day being drawn and inside the
 * span. Null otherwise — and null is the common case, because most days a
 * clinic looks at are not today.
 */
export function nowLineMinutes(
  frame: DayFrame,
  span: DaySpan,
  date: string,
  now: Date = new Date(),
): number | null {
  const here = wallClock(now.toISOString(), frame.timezone);
  if (here.date !== date) return null;
  if (here.minutes < span.startsAt || here.minutes > span.endsAt) return null;
  return here.minutes;
}

/**
 * The chair is occupied after the appointment ends, and this is that time
 * drawn.
 *
 * **The length comes from the appointment's own `turnoverMinutes`, never from
 * the branch's current default** (P3 Q1). Turnover is cleaning time for the
 * procedure that just finished, so it is a property of that booking — a long
 * extraction carries a longer clean-down than a check-up, and a buffer block
 * carries none. The value was copied onto the row when it was booked precisely
 * so that a clinic changing its default does not retroactively move work
 * already in the diary.
 *
 * The mockup's version of this hardcodes 15 minutes on every appointment, which
 * is the same fiction its `clashes()` encodes and which P3 Q1 rejected.
 */
export interface TurnoverStrip {
  /** The appointment whose turnover this is. */
  appointmentId: string;
  /** Minutes since local midnight, where the appointment ended. */
  startsAt: number;
  /** How long the strip actually draws for — clamped, so not always the full turnover. */
  minutes: number;
}

/**
 * Under this many minutes a strip is a smear rather than a shape, and reads as
 * a rendering artefact instead of as information. The mockup draws the same
 * line at the same number.
 *
 * A **rendering** threshold, not a clinical one: the chair is still occupied
 * for those minutes and the conflict service still refuses to book into them.
 * This only decides whether the grid bothers to draw the fact.
 */
export const MIN_VISIBLE_TURNOVER = 6;

/**
 * Turnover strips for **one chair's** appointments.
 *
 * One chair at a time, because turnover is only ever interrupted by the next
 * booking *in the same chair* — a chair's occupancy has nothing to say about
 * its neighbour's.
 *
 * Three things shorten a strip, and it draws at the smallest:
 *
 * - **its own turnover**, the full length when nothing is in the way;
 * - **the next booking in this chair**, so a strip never draws under a block —
 *   which happens legitimately whenever a buffer block sits flush against the
 *   appointment before it, since a block turns over at zero;
 * - **the bottom of the grid**, because turnover may legitimately run past
 *   closing (`opening-hours.ts`) and the drawn day ends at the last whole hour.
 *   The rule is unchanged; only the drawing stops.
 *
 * A cancelled appointment produces no strip and blocks nobody else's: the chair
 * is free, which is the whole point of the slot having been cancelled.
 */
export function turnoverStripsForChair(
  appointments: readonly Appointment[],
  timezone: string,
  date: string,
  span: DaySpan,
): TurnoverStrip[] {
  const placed = appointments
    // **Not just cancelled — a no-show frees the chair too** (§6.1, and the
    // server's own `NON_BLOCKING_STATUSES`). Drawing a strip after a no-show
    // would claim the chair was being cleaned after a patient who never sat in
    // it, and would disagree with what the conflict service will allow there.
    .filter((appointment) => blocksTheChair(appointment))
    .flatMap((appointment) => {
      const at = placeOnDay(appointment, timezone, date);
      return at === null ? [] : [{ appointment, at }];
    });

  const strips: TurnoverStrip[] = [];

  for (const { appointment, at } of placed) {
    // Zero turnover is a buffer block, and it draws nothing. That falls out of
    // the arithmetic rather than being a special case, which is the point:
    // there is one rule and blocks are not an exception to it.
    let minutes = Math.min(appointment.turnoverMinutes, span.endsAt - at.endsAt);

    for (const other of placed) {
      if (other.appointment.id === appointment.id) continue;
      if (other.at.startsAt < at.endsAt) continue;
      minutes = Math.min(minutes, other.at.startsAt - at.endsAt);
    }

    if (minutes < MIN_VISIBLE_TURNOVER) continue;

    strips.push({ appointmentId: appointment.id, startsAt: at.endsAt, minutes });
  }

  return strips;
}

// ── Dragging (STORY-034) ────────────────────────────────────────────────────

/**
 * Drag snaps to a quarter of an hour — working plan §6.1, and the mockup does
 * the same.
 *
 * **A rendering constant, not clinic data.** It describes how precisely a
 * pointer may express an intention, not anything a practice configures. It is
 * deliberately *not* related to the fifteen minutes that used to be the default
 * turnover; that number moved to the branch record and this one did not.
 */
export const SNAP_MINUTES = 15;

/**
 * The statuses that stop holding a chair the moment they are set.
 *
 * **Mirrors `NON_BLOCKING_STATUSES` on the server**, and must keep mirroring
 * it: a status treated as free here but busy there produces a drag that accepts
 * a drop the server then refuses, which is the one failure mode this whole
 * mechanism exists to avoid. Working plan §6.1 — a cancelled slot is free
 * immediately, and so is a no-show.
 */
const NON_BLOCKING: readonly string[] = ["CANCELLED", "NO_SHOW"];

export function blocksTheChair(appointment: Appointment): boolean {
  return !NON_BLOCKING.includes(appointment.status);
}

export interface MoveProposal {
  /** Minutes since local midnight, snapped and clamped. */
  startsAt: number;
  /** Which column the block would land in. */
  chairIndex: number;
  /**
   * Whether the server would accept it.
   *
   * **A prediction, not a decision.** It is built from occupancy the server
   * already sent, so it agrees with the server as long as nothing changed since
   * the day was read — and when something did, the drop is refused and the
   * block goes back. The point is to reject during the drag rather than after
   * it, which §6.1 asks for.
   */
  legal: boolean;
}

export interface MoveInput {
  appointment: Appointment;
  /** Every appointment on the day, in every chair. */
  all: readonly Appointment[];
  chairIds: readonly string[];
  frame: DayFrame;
  span: DaySpan;
  date: string;
  /** Pointer travel since the drag began, in pixels. */
  dx: number;
  dy: number;
  /** Measured live, because a column's width depends on the window. */
  columnWidth: number;
}

/**
 * Where a dragged block would land, and whether it may.
 *
 * ── The clamp ───────────────────────────────────────────────────────────────
 *
 * To the branch's opening, and to **closing minus the booking's own duration**
 * (§6.1) — so a block dragged off the bottom stops at the last slot it fits in
 * rather than hanging over the end of the day. Turnover is not counted: cleaning
 * the chair afterwards is exactly when cleaning happens.
 *
 * **It clamps to the drawn span, not to the branch's hours** (H2, 2026-08-25).
 * Hours refuse nothing now, so clamping to them would stop a drag the server
 * would have accepted — the drawn span is both what the user can see and what
 * the reschedule picker offers against, which keeps the mouse and the dialog
 * agreeing. The span always contains the branch's hours, so a drag can still
 * reach every minute the clinic says it is open.
 *
 * ── Snapping to a neighbour, not just to the quarter-hour ──────────────────
 *
 * The 15-minute grid and a neighbouring booking's turnover boundary almost
 * never line up — a booking that ends at 12:07 with a 15-minute turnover frees
 * its chair at 12:22, a minute the grid cannot express. Snapping only to the
 * grid left two bad outcomes: landing *inside* the turnover was flatly
 * refused, and landing just past it left whatever gap the grid happened to
 * leave — 8 minutes, 13, whatever the boundary's remainder was — dressed up as
 * a deliberate choice (client request, 2026-08-28: "as close as possible
 * without breaking the turnover rule", not a random leftover).
 *
 * So the grid mark is one *candidate* start time among several: for every
 * booking already in the target chair, the instant its turnover clears and the
 * latest instant that still clears *before* it starts are candidates too.
 * Whichever legal candidate sits closest to where the pointer actually is
 * wins. In open space every neighbour's candidates are far from the pointer,
 * so the familiar quarter-hour grid wins by default — this only pulls a drop
 * tight against a neighbour once the drag is genuinely near one.
 *
 * **`MAGNET_RANGE_MINUTES` caps how far that winning candidate may be from
 * the pointer, checked on the answer rather than on which neighbours are
 * asked.** Filtering neighbours by proximity first was tried and is wrong: a
 * run of back-to-back bookings chains, so a neighbour whose *near* edge
 * qualifies can still hand back its *far* edge, which is nowhere near the
 * pointer. Checking the chosen candidate directly closes that hole — dropping
 * into the middle of an hours-long packed run cannot teleport to whichever
 * free edge of it is arithmetically closest; it stays refused, exactly as an
 * ungapped drop always has, rather than relocating somewhere the user never
 * pointed at.
 */
const MAGNET_RANGE_MINUTES = 60;
export function proposeMove(input: MoveInput): MoveProposal {
  const { appointment, all, chairIds, frame, span, date, dx, dy, columnWidth } = input;

  const placed = placeOnDay(appointment, frame.timezone, date);
  const duration = placed === null ? 0 : placed.endsAt - placed.startsAt;
  const from = placed?.startsAt ?? span.startsAt;

  const opens = span.startsAt;
  const closes = span.endsAt;
  const clamp = (minute: number): number => Math.max(opens, Math.min(closes - duration, minute));

  // The pointer's own answer, continuous and unsnapped — what every candidate
  // below is measured against for closeness.
  const pointed = clamp(from + dy / span.pxPerMinute);
  const gridSnapped = clamp(from + Math.round(dy / span.pxPerMinute / SNAP_MINUTES) * SNAP_MINUTES);

  const currentIndex = chairIds.indexOf(appointment.chairId);
  const chairIndex = Math.max(
    0,
    Math.min(chairIds.length - 1, currentIndex + Math.round(dx / columnWidth)),
  );

  const chairId = chairIds[chairIndex] ?? appointment.chairId;

  const neighbours = all.flatMap((other) => {
    if (other.id === appointment.id) return [];
    if (other.chairId !== chairId) return [];
    if (!blocksTheChair(other)) return [];

    const at = placeOnDay(other, frame.timezone, date);
    return at === null
      ? []
      : [{ startsAt: at.startsAt, occupiedUntil: at.endsAt + other.turnoverMinutes }];
  });

  // Half-open on both sides, exactly as the server does it: a booking starting
  // at the exact minute another's turnover ends is legal, and is the commonest
  // booking in a busy chair.
  const isLegal = (candidate: number): boolean => {
    const occupiedUntil = candidate + duration + appointment.turnoverMinutes;
    return !neighbours.some((n) => candidate < n.occupiedUntil && n.startsAt < occupiedUntil);
  };

  // Every neighbour offers both of its edges as a candidate — filtering by
  // "is this neighbour near the pointer" first was tried and is wrong: a run
  // of back-to-back bookings chains, so a neighbour whose *near* edge is close
  // enough to qualify can still contribute its *far* edge, which is not close
  // to anything. The range check below applies to the chosen candidate, not
  // to which neighbours are asked.
  const candidates = [
    gridSnapped,
    ...neighbours.flatMap((n) => [
      n.occupiedUntil, // flush after this neighbour
      n.startsAt - duration - appointment.turnoverMinutes, // flush before it
    ]),
  ]
    .map(clamp)
    .filter(isLegal);

  // Strictly-less, not less-or-equal, so an exact tie keeps whichever
  // candidate was listed first — the array above puts each neighbour's
  // "flush after" ahead of its "flush before", which is what makes landing
  // exactly between two equally-close options move forward in time rather
  // than back.
  const best =
    candidates.length === 0
      ? gridSnapped
      : candidates.reduce((closest, candidate) =>
          Math.abs(candidate - pointed) < Math.abs(closest - pointed) ? candidate : closest,
        );

  // **The distance check happens here, on the answer, not on the neighbours
  // that produced it.** `best` is always within `SNAP_MINUTES / 2` of the
  // pointer when the plain grid mark was legal — this only ever discards a
  // *rescue* that reached further than a drag near one specific neighbour
  // plausibly means, which is what stops a drop into the middle of an
  // hours-long packed run from teleporting to whichever free edge of it is
  // arithmetically closest.
  const withinReach = Math.abs(best - pointed) <= MAGNET_RANGE_MINUTES;

  return {
    startsAt: withinReach ? best : gridSnapped,
    chairIndex,
    legal: candidates.length > 0 && withinReach,
  };
}

/**
 * Can this booking be dragged at all?
 *
 * **Completed and cancelled are fixed** (§6.1): a completed visit has an
 * invoice raised against it and work charted on the tooth chart, and a
 * cancelled slot may already have gone to somebody on the waitlist. The server
 * refuses both with `reason: NOT_MOVABLE`; this only stops the pointer picking
 * them up, so the refusal is never a surprise.
 */
export function isMovable(appointment: Appointment): boolean {
  return appointment.status !== "COMPLETED" && appointment.status !== "CANCELLED";
}

/**
 * Which local day an instant falls on in the branch's zone, as `YYYY-MM-DD`.
 *
 * The same frame `VisitsService` resolves a visit's date in (P2 Q8), which is
 * what makes comparing the two meaningful — a visit dated in the branch's zone
 * and an appointment dated in the reader's would disagree either side of
 * midnight.
 *
 * **Moved here from `appointment-panel.tsx` at STORY-057**, where the queue
 * needs it too: its rows are not bound to the day on screen, so each one has to
 * say which day it is, and the calendar's day input takes this exact shape.
 */
export function localDayOf(instant: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(instant));

  const field = (type: string): string => parts.find((part) => part.type === type)?.value ?? "";
  return `${field("year")}-${field("month")}-${field("day")}`;
}

/**
 * The MIME type a dragged waitlist entry travels under (Anthony's QA, item 14).
 *
 * ── Why a custom type and not `text/plain` ──────────────────────────────────
 *
 * The grid checks `dataTransfer.types` before it calls `preventDefault`, and
 * that check is what stops it becoming a drop target for everything else a
 * browser can drag — a selected patient name, a file from the desktop, a link
 * from another tab. Under `text/plain` all of those would land on a chair
 * column and open a booking form with a garbage id.
 *
 * The value carried is the entry id and nothing else. A patient's name in a
 * drag payload is readable by any page the drag passes over.
 *
 * **Lower case, and that is not style.** The drag-and-drop spec normalises type
 * strings to lower case on the way in, so a mixed-case constant would be set
 * under one name and looked up under another — the drop silently never firing.
 */
export const WAITLIST_DRAG_TYPE = "application/x-dentacore-waitlist-entry";

/**
 * **A booking card is always tall enough for its two lines** (CZ-D3, Emile,
 * 2026-10-21: *"always make sure the appointments has enough height to fit all
 * the needed text"*): the name, then time · who · what, with room above and
 * below. Shorter bookings draw taller than their minutes, and `packLanes` sets
 * them side by side where that would make two cards overlap.
 */
export const CARD_MIN_PX = 48;

/**
 * **An hour is never drawn shorter than this** (CZ-D4, Emile, 2026-10-21): a
 * half-hour visit then holds a whole card with its gap. Where a day's hours do
 * not fit the window at this height, the grid shows as many as do and pages
 * through the rest (CZ-D1) — it never squeezes them and never scrolls.
 */
export const MIN_HOUR_PX = Math.ceil(((CARD_MIN_PX + 4) / 30) * 60);

/**
 * Lanes for cards that overlap **as drawn** — each `[lane, lanes]` — so
 * overlapping cards stand next to each other rather than one on top of the
 * other (CZ-D3). A cluster is a run of cards each overlapping the next; every
 * card in it is as wide as the cluster's widest lane count allows.
 */
export function packLanes(
  items: readonly { id: string; startsAt: number; endsAt: number }[],
): Map<string, [number, number]> {
  const lane = new Map<string, number>();
  const out = new Map<string, [number, number]>();
  let cluster: (typeof items)[number][] = [];
  let end = -Infinity;
  const close = (): void => {
    const lanes = Math.max(...cluster.map((item) => lane.get(item.id) ?? 0)) + 1;
    for (const item of cluster) out.set(item.id, [lane.get(item.id) ?? 0, lanes]);
    cluster = [];
  };
  for (const item of [...items].sort((a, b) => a.startsAt - b.startsAt)) {
    if (item.startsAt >= end && cluster.length > 0) close();
    const used = cluster
      .filter((other) => other.endsAt > item.startsAt)
      .map((other) => lane.get(other.id));
    let free = 0;
    while (used.includes(free)) free += 1;
    lane.set(item.id, free);
    cluster.push(item);
    end = Math.max(end, item.endsAt);
  }
  if (cluster.length > 0) close();
  return out;
}
